import { CSSProperties, forwardRef, useLayoutEffect, useRef } from 'react';
import { DismissButton, FocusScope, useOverlay } from 'react-aria';
import { ParentZIndexProvider } from 'src/hooks/use-parent-z-index';
import { useWhileActive } from 'src/hooks/use-while-active';
import styled from 'styled-components';

const zIndex = 1;

const Overlay = styled.div`
  z-index: ${zIndex};
  position: absolute;
  width: 100%;
`;

const PopoverWrapper = styled.div`
  display: inline-flex;
  position: relative;
  width: 100%;
  max-width: 100%;
`;

type PopoverProps = {
  children: React.ReactNode;
  onClose: () => void;
  style?: CSSProperties;
};

export const Popover = forwardRef<HTMLDivElement, PopoverProps>(
  (props, ref) => {
    const overlayRef = useRef<HTMLDivElement>(null);
    const { onClose, children, style } = props;

    const [active] = useWhileActive(overlayRef, {
      initialActive: true,
    });

    const { overlayProps } = useOverlay(
      {
        isOpen: true,
        onClose,
        // instead of using `shouldCloseOnBlur` we need our custom `useWhileActive`
        // or else the popover would close while scrolling the options (e.g. in our date picker)
        shouldCloseOnBlur: false,
        isDismissable: false,
        isKeyboardDismissDisabled: true,
      },
      overlayRef
    );

    useLayoutEffect(() => {
      if (!active) {
        onClose();
      }
    }, [active, onClose]);

    // hidden <DismissButton> allows screen reader users to dismiss the popup easily.
    return (
      <PopoverWrapper style={style} ref={ref}>
        <FocusScope restoreFocus>
          <ParentZIndexProvider value={zIndex}>
            <Overlay {...overlayProps} ref={overlayRef}>
              {children}
              <DismissButton onDismiss={onClose} />
            </Overlay>
          </ParentZIndexProvider>
        </FocusScope>
      </PopoverWrapper>
    );
  }
);
